import React, { createContext, useCallback, useContext, useState } from 'react';

export type ErrorInstance = {
  statusCode?: number;
  contentKey?: string;
  props?: Record<string, string>;
};
export type AlertType = 'success' | 'danger';
export type AlertProps = {
  type?: AlertType;
  message?: string;
  props?: Record<string, string>;
};
type ErrorProps = {
  alert?: AlertProps;
  error?: ErrorInstance;
  updateAlert?: (input: AlertProps) => void;
  updateError?: (input: ErrorInstance) => void;
};
type ErrorProviderProps = {
  children: React.ReactNode;
  values: ErrorProps;
};

const ErrorContext = createContext<ErrorProps | undefined>(undefined);
const ErrorProvider = ({ children, values }: ErrorProviderProps) => {
  const [alert, setAlert] = useState(values?.alert ?? {});
  const [error, setError] = useState(values?.error);
  const updateAlert = useCallback(input => setAlert(input), [setAlert]);
  const updateError = useCallback(input => setError(input), [setError]);

  return (
    <ErrorContext.Provider
      value={{
        alert,
        error,
        updateAlert: values.updateAlert ?? updateAlert,
        updateError: values.updateError ?? updateError
      }}
    >
      {children}
    </ErrorContext.Provider>
  );
};

const useAlert = () => {
  const context = useContext(ErrorContext);

  if (context === undefined) {
    throw new Error('useAlert must be called within a provider.');
  }

  const { alert, updateAlert } = context;
  return { alert, updateAlert };
};

const useError = () => {
  const context = useContext(ErrorContext);

  if (context === undefined) {
    throw new Error('useError must be called within a provider.');
  }

  const { error, updateError } = context;
  return { error, updateError };
};

export { ErrorProvider, useAlert, useError };
